import { Logger } from '@fnv-core/logging';

type EventCallback = (data: any) => void;

type EventCallbackOptions = {
  /**
   * Set to true if the callback should only be fired once per event.
   */
  once?: boolean;
};

type EventListener = {
  callback: EventCallback;
  options: EventCallbackOptions;
};

/**
 * A simple event bus for observer/subscriber pattern.
 */
export class EventManager {
  private static _listeners: { [eventName: string]: EventListener[] } = {};

  private static _publishedEvents: { [eventName: string]: any } = {};

  /**
   * Clears/removes all listeners and events.
   */
  static clear() {
    this._listeners = {};
    this._publishedEvents = {};
  }

  /**
   * Add a callback to the given event name.
   * 
   * @param eventName   The name of the event to listen to.
   * @param callback 
   */
  static subscribe(eventName: string, callback: EventCallback, options?: EventCallbackOptions): void {
    if (!this._listeners[eventName]) {
      this._listeners[eventName] = [];
    }

    const callbackOptions = {
      // defaults
      once: false,
      // overrides
      ...options,
    };

    const eventAlreadyPublished = !!this._publishedEvents[eventName];
    if (eventAlreadyPublished) {
      // Already published, callback immediately
      callback(this._publishedEvents[eventName]);
    }
    
    if (!eventAlreadyPublished || !callbackOptions.once) {
      this._listeners[eventName].push({ callback, options: callbackOptions });
    }
  }

  /**
   * Publishes the given event name to invoke all listeners.
   * 
   * @param eventName   The name of the event to invoke.
   * @param data        Optional data to pass to the listeners.
   */
  static publish(eventName: string, data?: any): void {

    Logger.info(`Publishing event for '${eventName}' with data '${data != null ? JSON.stringify(data) : '<nothing>'}'`);

    // Keep track of publishing so that subscribers can be invoked even after the event is already published.
    this._publishedEvents[eventName] = data || {};

    const eventListeners = this._listeners[eventName];
    if (!eventListeners) {
      return;
    }

    eventListeners.forEach((listener: EventListener) => {
      listener.callback(data);
    });

    // Remove all listeners that were registered as 'once'
    this._listeners[eventName] = eventListeners.filter(listener => !listener.options.once);
  }
}
